import React, { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Avatar } from '@components/Avatar/Avatar'
import { Box, BoxProps, Heading, Paragraph, Stack, Text } from '@ui'
import { formatUnitsToFixedLength } from 'hooks/useBalance'
import { useBotMetadataQuery } from 'hooks/useBotMetadataQuery'
import { type Address, useBotData } from 'use-towns-client'

const numberFormatter = new Intl.NumberFormat('en-US')

// Simple container for link unfurls - matches the pattern used in MessageAttachments
const LinkContainer = (props: BoxProps) => (
    <Box background="level2" borderRadius="md" maxWidth="300" {...props} />
)

// Pill component for stats - matches the pattern from TownsContent
const Pill = ({ children, ...props }: BoxProps) => (
    <Box
        background="lightHover"
        borderRadius="md"
        paddingY="sm"
        paddingX="paragraph"
        {...props}
        justifyContent="center"
        height="height_md"
        overflow="hidden"
    >
        <Text truncate size="sm" color="gray1" whiteSpace="nowrap">
            {children}
        </Text>
    </Box>
)

export const BotContent = (props: { botPath: string; appAddress: string }) => {
    const { botPath, appAddress } = props

    const { data: bot } = useBotData(appAddress as Address)
    const { data: botMetadata, isLoading } = useBotMetadataQuery(appAddress as Address)

    const navigate = useNavigate()

    const onClick = useCallback(() => {
        navigate(botPath, { state: { fromLink: true } })
    }, [navigate, botPath])

    // Only calculate price if bot data exists
    const isBotFree = bot ? bot.installPrice <= 0n : false
    const price = bot && !isBotFree ? formatUnitsToFixedLength(bot.installPrice, 18, 4) : null
    const showPrice = bot !== undefined // Show price pill for both free and paid bots
    const totalUsers = botMetadata?.totalUsers ?? 0
    const totalUsersFormatted = numberFormatter.format(totalUsers)

    // Don't render if bot data hasn't loaded yet
    if (!bot) {
        return null
    }

    return (
        <LinkContainer
            horizontal
            hoverable
            padding="sm"
            minWidth={{ mobile: '300', default: '150' }}
            gap="sm"
            maxWidth={{ mobile: '300', default: '400' }}
            width="100%"
            cursor="pointer"
            onClick={onClick}
        >
            {/* Bot Avatar - matching InteractiveSpaceIcon size */}
            <Box
                minWidth="x7"
                width="x7"
                height="x7"
                overflow="hidden"
                borderRadius="sm"
                background="level3"
            >
                {bot.metadata?.imageUrl ? (
                    <img
                        src={bot.metadata.imageUrl}
                        alt={bot.metadata?.displayName ?? bot.metadata?.username ?? 'Bot'}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    <Avatar size="avatar_md" userId={appAddress} />
                )}
            </Box>

            {/* Bot Info - matching TownsContent structure */}
            <Box gap="paragraph" paddingY="sm" paddingRight="sm" overflow="hidden">
                <Stack gap="paragraph">
                    {/* Bot Name - using Heading like TownsContent */}
                    <Heading level={3} color="default" whiteSpace="normal">
                        {bot.metadata?.displayName ?? bot.metadata?.username ?? 'Bot'}
                    </Heading>

                    {/* Bot Description - using Paragraph like TownsContent */}
                    {bot.metadata?.description && (
                        <Paragraph color="gray2">{bot.metadata.description}</Paragraph>
                    )}
                </Stack>

                {/* Bot Stats in Pills - matching TownsContent pattern */}
                {!isLoading && (
                    <Stack horizontal flexWrap="wrap" gap="sm">
                        {totalUsers > 0 && (
                            <Pill>
                                {totalUsersFormatted} {totalUsers === 1 ? 'user' : 'users'}
                            </Pill>
                        )}
                        {showPrice && <Pill>{isBotFree ? 'Free' : `${price} ETH`}</Pill>}
                    </Stack>
                )}
            </Box>
        </LinkContainer>
    )
}
